import { ApolloError } from '@apollo/client';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import { ActionFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  AnswerSmartFillFormBucketDocument,
  AnswerSmartFillFormBucketMutation,
  AnswerSmartFillFormBucketMutationVariables,
  GenerateSmartFillWorkspaceBucketDocument,
  GenerateSmartFillWorkspaceBucketMutation,
  GenerateSmartFillWorkspaceBucketMutationVariables
} from '~/api/graphql';

export const answerSmartFillFormBucket = (variables: AnswerSmartFillFormBucketMutationVariables) =>
  mutateData<AnswerSmartFillFormBucketMutation, AnswerSmartFillFormBucketMutationVariables>(
    client,
    { mutation: AnswerSmartFillFormBucketDocument, variables }
  );

export const generateSmartFillWorkspaceBucket = (
  variables: GenerateSmartFillWorkspaceBucketMutationVariables
) =>
  mutateData<
    GenerateSmartFillWorkspaceBucketMutation,
    GenerateSmartFillWorkspaceBucketMutationVariables
  >(client, { mutation: GenerateSmartFillWorkspaceBucketDocument, variables });

/** What the bucket page posts; the bucket comes from the URL. */
export type SmartFillAnswersSubmission = Omit<
  AnswerSmartFillFormBucketMutationVariables,
  'bucketId'
>;

const SESSION_EXPIRED = 'Your session expired. Sign in again in another tab, then save.';

/**
 * Like submitMutation, except that every failure comes back as a result the page
 * shows. Typed answers live only in the page until they save, and the sign-in
 * redirect or the error screen would throw them away.
 */
export const submitAnswerSmartFillFormBucket: ActionFunction = async ({ params, request }) => {
  const submission = (await request.json()) as SmartFillAnswersSubmission;
  try {
    const result = await answerSmartFillFormBucket({
      ...submission,
      bucketId: params.bucketId ?? ''
    });
    if (getApplicationError(result.error)?.code === ApplicationErrorCode.Unauthorized) {
      return {
        applicationError: { code: ApplicationErrorCode.Unauthorized, message: SESSION_EXPIRED }
      };
    }
    return await submitMutation(Promise.resolve(result));
  } catch (error) {
    return {
      serverError:
        error instanceof ApolloError ? error : new ApolloError({ errorMessage: `${error}` })
    };
  }
};
